/**
 * The Responses table's columns: what each shows, filters and sorts by. Ported
 * from the mock's `presentation/form/responseColumns.ts`; keep the names
 * diffable against it.
 */
import { EDUCATION_LEVELS } from '@chat-emp/shared';

import { answersTo, type ResponseProfile, type ResponseRow } from '../data/responses.js';
import type { Form, FormField } from '../fixtures/domain.js';
import { formatRinggit, toDateInput } from '../util/format.js';

/** Picks the sort icons: digits for anything ordered by amount or rank, letters for words. */
export type SortKind = 'number' | 'text';

export interface ResponseColumn {
  key: string;
  label: string;
  /** Where the View menu lists it while hidden. */
  group: 'details' | 'form' | 'others';
  /** Always shown and always first: never in the View menu, never dragged. */
  pinned?: boolean;
  /** By unticking values, by a phrase, by a range of days, or by a range of amounts. */
  filter: 'values' | 'text' | 'date' | 'number' | 'none';
  /** What the row holds, for display, ticking and searching. Empty means blank. */
  values: (row: ResponseRow) => string[];
  /** The word a blank value goes by in the filter list, and in the cell unless `blankCell`. */
  blank: string;
  /** What an empty cell shows instead, where the word would be noise down the column. */
  blankCell?: string;
  width: number;
  /** Absent where order means nothing: written answers, several picks at once. */
  sort?: { kind: SortKind; key: (row: ResponseRow) => string | number | null };
  /** The order a filter lists values in, where the data has its own. */
  order?: readonly string[];
  pills?: boolean;
  align?: 'right';
  /** A moment, drawn as date and time; not searched or ticked. */
  when?: (row: ResponseRow) => string;
  /** The amount a `number` filter reads; null when there is none. */
  amount?: (row: ResponseRow) => number | null;
}

/** A range is whole days, `YYYY-MM-DD` in the reader's timezone; either end may be open. */
export type ColumnFilter =
  | { hidden: ReadonlySet<string> }
  | { contains: string }
  | { range: { from?: string; to?: string } }
  | { between: { min?: number; max?: number } };
export type Filters = ReadonlyMap<string, ColumnFilter>;
export interface Sort {
  key: string;
  descending: boolean;
}

/** Newest answers first, until somebody asks for another order. */
export const DEFAULT_SORT: Sort = { key: 'submitted', descending: true };

export function isDefaultSort(sort: Sort): boolean {
  return sort.key === DEFAULT_SORT.key && sort.descending === DEFAULT_SORT.descending;
}

export function idOf(row: ResponseRow): string {
  return row.submission.response.id;
}

const NOT_PROVIDED = 'Not provided';

function profileColumn(
  key: string,
  label: string,
  read: (profile: ResponseProfile) => string[],
  extra: Partial<ResponseColumn> & {
    rank?: { kind: SortKind; by: (profile: ResponseProfile) => number | string | null };
  } = {},
): ResponseColumn {
  const { rank, ...rest } = extra;
  return {
    key,
    label,
    group: 'details',
    filter: 'values',
    blank: NOT_PROVIDED,
    width: 150,
    values: (row) => (row.person.profile ? read(row.person.profile) : []),
    ...(rank && {
      sort: {
        kind: rank.kind,
        key: (row: ResponseRow) => (row.person.profile ? rank.by(row.person.profile) : null),
      },
    }),
    ...rest,
  };
}

/** The jobseeker first, the profile facts an employer screens on, every question, then when. */
export function columnsFor(form: Form): ResponseColumn[] {
  return [
    {
      key: 'person',
      label: 'Jobseeker',
      group: 'details',
      pinned: true,
      filter: 'none',
      blank: 'Jobseeker',
      width: 220,
      values: (row) => listOf(row.person.name),
      sort: { kind: 'text', key: (row) => row.person.name ?? null },
    },
    {
      key: 'job',
      label: 'Job',
      group: 'details',
      filter: 'values',
      blank: 'No job',
      blankCell: '—',
      width: 220,
      values: (row) => listOf(row.submission.jobTitle),
      sort: { kind: 'text', key: (row) => row.submission.jobTitle ?? null },
    },
    profileColumn(
      'salary',
      'Expected salary',
      (profile) =>
        profile.expectedSalary === undefined ? [] : [formatRinggit(profile.expectedSalary)],
      {
        rank: { kind: 'number', by: (profile) => profile.expectedSalary ?? null },
        align: 'right',
        filter: 'number',
        amount: (row) => row.person.profile?.expectedSalary ?? null,
      },
    ),
    profileColumn('livesIn', 'Lives in', (profile) => listOf(profile.city), {
      rank: { kind: 'text', by: (profile) => profile.city ?? null },
    }),
    profileColumn('education', 'Education', (profile) => listOf(profile.education), {
      rank: {
        kind: 'number',
        by: (profile) => (profile.education ? educationRank(profile.education) : null),
      },
      order: EDUCATION_LEVELS,
    }),
    profileColumn('languages', 'Languages', (profile) => profile.languages, { width: 220 }),
    ...form.fields.map(questionColumn),
    momentColumn('submitted', 'Submitted at', (row) => row.submission.response.submittedAt),
    momentColumn('sent', 'Sent at', (row) => row.submission.sentAt),
  ];
}

/** Where a rung sits on the ladder the inbox filter offers; one the ladder lacks goes last. */
function educationRank(education: string): number {
  const at = (EDUCATION_LEVELS as readonly string[]).indexOf(education);
  return at === -1 ? EDUCATION_LEVELS.length : at;
}

function momentColumn(
  key: string,
  label: string,
  when: (row: ResponseRow) => string,
): ResponseColumn {
  return {
    key,
    label,
    group: 'others',
    filter: 'date',
    blank: '',
    width: 180,
    values: () => [],
    when,
    sort: { kind: 'number', key: (row) => Date.parse(when(row)) },
  };
}

function questionColumn(field: FormField): ResponseColumn {
  const labels = field.options.map((option) => option.label);
  const rank = (label: string) => {
    const at = labels.indexOf(label);
    return at === -1 ? labels.length : at;
  };
  const values = (row: ResponseRow) =>
    [...(answersTo(row.submission, field) ?? [])].sort((a, b) => rank(a) - rank(b));
  return {
    key: `q:${field.id}`,
    label: field.label,
    group: 'form',
    filter: 'values',
    blank: 'Not asked',
    width: 240,
    values,
    pills: true,
    order: labels,
    // Single choice ranks by where its pick sits in the question's own list.
    ...(field.type === 'choice' && {
      sort: {
        kind: 'number' as const,
        key: (row: ResponseRow) => {
          const [pick] = values(row);
          return pick === undefined ? null : rank(pick);
        },
      },
    }),
  };
}

/** Only the name and the questions show until the employer asks for more. */
export function shownByDefault(column: ResponseColumn): boolean {
  return column.group === 'form';
}

function listOf(value: string | undefined): string[] {
  return value ? [value] : [];
}

/** A blank cell is ticked under its blank word, so "Not provided" can be sieved too. */
function tickValues(column: ResponseColumn, row: ResponseRow): string[] {
  const values = column.values(row);
  return values.length > 0 ? values : [column.blank];
}

function passes(column: ResponseColumn, filter: ColumnFilter, row: ResponseRow): boolean {
  if ('between' in filter) {
    const amount = column.amount?.(row) ?? null;
    const { min, max } = filter.between;
    return (
      amount !== null &&
      (min === undefined || amount >= min) &&
      (max === undefined || amount <= max)
    );
  }
  if ('range' in filter) {
    if (!column.when) return true;
    const day = toDateInput(new Date(column.when(row)));
    const { from, to } = filter.range;
    return (!from || day >= from) && (!to || day <= to);
  }
  if ('contains' in filter) {
    const term = filter.contains.trim().toLowerCase();
    return term === '' || column.values(row).some((value) => value.toLowerCase().includes(term));
  }
  return tickValues(column, row).some((value) => !filter.hidden.has(value));
}

export function isActive(filter: ColumnFilter | undefined): boolean {
  if (!filter) return false;
  if ('range' in filter) return Boolean(filter.range.from || filter.range.to);
  if ('between' in filter)
    return filter.between.min !== undefined || filter.between.max !== undefined;
  return 'contains' in filter ? filter.contains.trim() !== '' : filter.hidden.size > 0;
}

/** The search over what is on screen, then every filter but `except`'s own, so a
 *  column's counts say what ticking would give. */
export function visibleRows(
  rows: ResponseRow[],
  {
    columns,
    filters,
    query = '',
    searchIn = [],
    except,
  }: {
    columns: ResponseColumn[];
    filters: Filters;
    query?: string;
    searchIn?: ResponseColumn[];
    except?: string;
  },
): ResponseRow[] {
  const term = query.trim().toLowerCase();
  const applied = columns.filter(
    (column) => column.key !== except && isActive(filters.get(column.key)),
  );
  return rows.filter(
    (row) =>
      (term === '' ||
        searchIn.some((column) =>
          column.values(row).some((value) => value.toLowerCase().includes(term)),
        )) &&
      applied.every((column) => passes(column, filters.get(column.key)!, row)),
  );
}

/** Each value a column holds across `rows`, with how many rows hold it. */
export function valueCounts(column: ResponseColumn, rows: ResponseRow[]): [string, number][] {
  const counts = new Map<string, number>();
  for (const row of rows) {
    for (const value of new Set(tickValues(column, row))) {
      counts.set(value, (counts.get(value) ?? 0) + 1);
    }
  }
  const place = (value: string) => {
    if (value === column.blank) return Number.MAX_SAFE_INTEGER;
    const at = column.order?.indexOf(value) ?? -1;
    return at === -1 ? Number.MAX_SAFE_INTEGER - 1 : at;
  };
  return [...counts].sort(
    ([a], [b]) => place(a) - place(b) || a.localeCompare(b, undefined, { numeric: true }),
  );
}

/** Each row's key is read once, not once per comparison. Blanks go last either way. */
export function sortRows(
  rows: ResponseRow[],
  columns: ResponseColumn[],
  sort: Sort,
): ResponseRow[] {
  const by = columns.find((column) => column.key === sort.key)?.sort;
  if (!by) return rows;
  const direction = sort.descending ? -1 : 1;
  return rows
    .map((row) => ({ row, key: by.key(row) }))
    .sort(({ key: left }, { key: right }) => {
      if (left === null || right === null) return left === right ? 0 : left === null ? 1 : -1;
      const order =
        typeof left === 'number' && typeof right === 'number'
          ? left - right
          : String(left).localeCompare(String(right), undefined, { numeric: true });
      return order * direction;
    })
    .map(({ row }) => row);
}
